import { useState } from "react";
import Breadcrumb from "@/components/feature/Breadcrumb";

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem("website_alt") as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus("success");
      form.reset();
      return;
    }

    const formData = new FormData(form);
    try {
      const res = await fetch("https://readdy.ai/api/form/d9pi1qhn2trhb35gvftg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: any;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === "OK") {
        setFormStatus("success");
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        if (serverMsg.includes("spam")) {
          setFormError("提交失败，请稍后重试");
        } else {
          setFormError(serverMsg || "提交失败，请稍后重试");
        }
        setFormStatus("error");
      }
    } catch {
      setFormError("网络错误，请稍后重试");
      setFormStatus("error");
    }
  };

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "51cg黑料揭秘",
    url: "https://51cgheibao.com",
    description: "致力于为用户提供51cg平台最真实、最全面、最及时的爆料信息与深度分析",
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer service",
      email: "contact@51cgheibao.com",
      availableLanguage: ["Chinese"],
    },
  };

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: "关于我们 - 51cg黑料揭秘",
    description: "51cg黑料揭秘的品牌介绍、口碑信誉和联系方式",
    url: "https://51cgheibao.com/about",
    inLanguage: "zh-CN",
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(webPageSchema)}</script>

      <main className="pt-20">
        <section className="py-10 md:py-14 px-4 md:px-8 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { label: "首页", href: "/" },
                { label: "关于我们" },
              ]}
            />
            <h1 className="text-3xl md:text-4xl font-bold text-foreground-950 mt-6 font-heading">
              关于我们
            </h1>
            <p className="text-sm text-foreground-600 mt-3 max-w-2xl">
              了解我们的使命、价值观和团队
            </p>
          </div>
        </section>

        <section className="py-14 md:py-18 px-4 md:px-8 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-2xl font-bold text-foreground-950 mb-6 font-heading">
                  品牌介绍
                </h2>
                <div className="space-y-4 text-sm text-foreground-700 leading-relaxed">
                  <p>
                    <strong className="text-foreground-950">51cg黑料揭秘</strong>是一个独立的信息聚合与深度分析平台，致力于为用户提供关于51cg平台最真实、最全面、最及时的爆料信息与深度分析。
                  </p>
                  <p>
                    我们的团队由资深调查记者、数据分析师和网络安全专家组成，通过多方信息交叉验证，为读者呈现经得起推敲的事实真相。
                  </p>
                  <p>
                    在这个信息泛滥的时代，我们坚持<strong className="text-foreground-950">独立、客观、真实</strong>的原则，不站队、不偏袒，只呈现事实，让读者自己做出判断。
                  </p>
                </div>
              </div>
              <div className="rounded-xl overflow-hidden h-72">
                <img
                  src="https://readdy.ai/api/search-image?query=Professional%20newsroom%20with%20warm%20lighting%2C%20investigative%20journalism%20atmosphere%2C%20team%20working%20on%20laptops%2C%20document%20boards%2C%20serious%20and%20focused%20mood%2C%20amber%20warm%20tones%2C%20cinematic%20composition%2C%20clean%20minimalist%20office&width=800&height=600&seq=about-brand-2026&orientation=landscape"
                  alt="51cg黑料揭秘团队"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-18 px-4 md:px-8 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-foreground-950 mb-10 font-heading text-center">
              我们的口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { stat: "50万+", label: "月活跃读者", icon: "ri-user-star-line" },
                { stat: "500+", label: "深度报道文章", icon: "ri-article-line" },
                { stat: "98%", label: "读者好评率", icon: "ri-thumb-up-line" },
              ].map((item, i) => (
                <div key={i} className="text-center p-8 rounded-xl bg-background-50">
                  <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className={`${item.icon} text-xl`}></i>
                  </div>
                  <div className="text-3xl font-bold text-primary-600 mb-2 font-heading">{item.stat}</div>
                  <div className="text-sm text-foreground-600">{item.label}</div>
                </div>
              ))}
            </div>

            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                {
                  quote: "终于找到一个能说真话的平台了！内容扎实，分析到位，帮我避免了一次投资损失。",
                  author: "张先生",
                  role: "互联网从业者",
                },
                {
                  quote: "每篇文章都经过深入调查，数据翔实、逻辑清晰。在这个信息混乱的时代，这样的平台太珍贵了。",
                  author: "李女士",
                  role: "金融分析师",
                },
                {
                  quote: "作为一个差点踩坑的人，真心感谢这个平台的存在。你们的报道让我看到了很多之前没注意到的问题。",
                  author: "王先生",
                  role: "普通用户",
                },
                {
                  quote: "内容专业且客观，既有爆料也有分析，不偏不倚。已经成为我了解行业动态的必备参考。",
                  author: "赵女士",
                  role: "媒体从业者",
                },
              ].map((item, i) => (
                <div key={i} className="p-6 rounded-xl bg-background-50">
                  <i className="ri-double-quotes-l text-2xl text-primary-300 mb-3 block"></i>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    {item.quote}
                  </p>
                  <div>
                    <div className="text-sm font-semibold text-foreground-950">{item.author}</div>
                    <div className="text-xs text-foreground-500">{item.role}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-18 px-4 md:px-8 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-foreground-950 mb-10 font-heading text-center">
              联系我们
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
              <div className="lg:col-span-2 space-y-6">
                <div>
                  <h3 className="text-sm font-semibold text-foreground-950 mb-2">联系方式</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    如果您有51cg平台相关的爆料信息、使用体验或维权经历，欢迎通过以下方式联系我们。您的信息将被严格保密。
                  </p>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-mail-line"></i>
                    </div>
                    <span className="text-sm text-foreground-700">contact@51cgheibao.com</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-shield-user-line"></i>
                    </div>
                    <span className="text-sm text-foreground-700">匿名爆料通道（加密传输）</span>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-3">
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  className="space-y-4"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        姓名
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        邮箱
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      主题
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      required
                      className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请简要描述您的来意"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      详细内容
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={5}
                      maxLength={500}
                      className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                      placeholder="请详细描述您的情况或提供爆料信息（不超过500字）"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                  </div>

                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  <button
                    type="submit"
                    className="w-full py-3 text-sm font-semibold bg-primary-500 text-background-50 rounded-lg hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    发送消息
                  </button>

                  {formStatus === "success" && (
                    <p className="text-sm text-green-600 text-center">
                      消息已成功发送！我们会尽快回复您。
                    </p>
                  )}
                  {formStatus === "error" && (
                    <p className="text-sm text-red-500 text-center">
                      {formError || "提交失败，请稍后重试"}
                    </p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}